import { CheckboxEditor, DropdownEditor, NumberEditor, RadioGroupEditor, TextEditor } from '@approvalmax/ui';
import { Box } from '@approvalmax/ui/src/components';
import { miscHelpers, numberHelpers } from '@approvalmax/utils';
import { netSuiteConstants } from 'modules/common/constants';
import { backend, domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { memo, useCallback, useMemo } from 'react';
import { useGetNetSuiteSubsidiaries } from 'shared/data/webApp/v1';

import { isVendorWebAddressValid, setCurrencyPrecision } from '../../helpers';
import { useCurrencyPrecision, useVendorTemplateFieldSettings } from '../../hooks';
import {
    isFieldMandatory,
    isFieldVisible,
    isOptionalFieldInvalid,
} from '../../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import {
    NetSuiteAccountsDataProvider,
    NetSuiteCustomFields,
    NetSuiteIncotermDataProvider,
    NetSuiteVendorCategoriesDataProvider,
} from '..';
import { useAddressListTable, useFields, useSubsidiaryTable } from './NetSuiteVendorForm.hooks';
import { messages } from './NetSuiteVendorForm.messages';
import {
    FieldsWrapper,
    InlineRadioEditor,
    RadioGroupEditorLabel,
    SectionHeader,
    StyledAddressLineItemsTable,
    StyledField,
    StyledLineItemsTable,
    WorkflowCurrenciesField,
    WorkflowStyledField,
} from './NetSuiteVendorForm.styles';
import { NetSuiteVendorFormProps } from './NetSuiteVendorForm.types';

export const NetSuiteVendorForm = memo<NetSuiteVendorFormProps>((props) => {
    const { companyId, terms, currencies, requestSubsidiary, showErrors, details, workflowId, isPopup = true } = props;

    const fieldSettings = useVendorTemplateFieldSettings();

    const { data: subsidiaryReferenceList, isFetching: isSubsidiaryFetching } = useGetNetSuiteSubsidiaries({
        query: {
            companyId,
        },
    });

    const defaultSubsidiary = useMemo(() => {
        const subsidiary = subsidiaryReferenceList?.values.find((item) => item.id === requestSubsidiary?.id);

        return subsidiary || null;
    }, [subsidiaryReferenceList, requestSubsidiary]);

    const defaultCurrency = useMemo(() => {
        const currency = currencies.find((currencyItem) => currencyItem.id === defaultSubsidiary?.currencyInternalId);

        return currency || null;
    }, [currencies, defaultSubsidiary]);

    const hasContactInformationSection = useMemo(
        () =>
            isFieldVisible(fieldSettings.email) ||
            isFieldVisible(fieldSettings.phone) ||
            isFieldVisible(fieldSettings.altPhone) ||
            isFieldVisible(fieldSettings.fax),
        [fieldSettings]
    );

    const hasFinancialInformationSection = useMemo(
        () =>
            isFieldVisible(fieldSettings.legalName) ||
            isFieldVisible(fieldSettings.accountNumber) ||
            isFieldVisible(fieldSettings.defaultExpenseAccount) ||
            isFieldVisible(fieldSettings.defaultPayablesAccount) ||
            isFieldVisible(fieldSettings.terms) ||
            isFieldVisible(fieldSettings.creditLimit) ||
            isFieldVisible(fieldSettings.incoterm) ||
            isFieldVisible(fieldSettings.taxNumber) ||
            isFieldVisible(fieldSettings.is1099Eligible) ||
            isFieldVisible(fieldSettings.isJobResource),
        [fieldSettings]
    );

    const Field = isPopup ? StyledField : WorkflowStyledField;
    const CurrenciesField = isPopup ? StyledField : WorkflowCurrenciesField;

    const { fieldValues, onFieldChange } = useFields({
        defaultSubsidiary,
        defaultCurrency,
        currencies,
        details,
        shouldDispatchChangeEvent: !isPopup,
    });

    const primarySubsidiaryItem = useMemo(() => {
        return fieldValues?.subsidiaries.find(
            ({ subsidiary }) => subsidiary?.id === fieldValues?.primarySubsidiary?.id
        );
    }, [fieldValues]);

    const currencyPrecision = useCurrencyPrecision(fieldValues?.currency);

    const {
        tableDef: subsidiaryTableDef,
        onSubsidiaryListItemAdd,
        onSubsidiaryListItemRemove,
        onSubsidiaryItemClone,
    } = useSubsidiaryTable({
        defaultSubsidiary,
        currencies,
        fieldSettings,
        showErrors,
        primarySubsidiaryId: fieldValues?.primarySubsidiary?.id,
        subsidiaryReferenceList: subsidiaryReferenceList?.values ?? [],
        shouldDispatchChangeEvent: !isPopup,
    });

    const {
        tableDef: addressTableDef,
        onAddressListItemAdd,
        onAddressListItemRemove,
        onAddressItemClone,
    } = useAddressListTable(fieldSettings, showErrors, !isPopup);

    const onPrimarySubsidiaryChange = useCallback(
        (value: domain.NetSuiteSubsidiaryItem) => {
            onFieldChange('primarySubsidiary')(value);
        },
        [onFieldChange]
    );

    const onPrimaryCurrencyChange = useCallback(
        (value: domain.NetSuiteCurrency | null) => {
            const newCurrencyPrecision = value?.precision ?? 2;

            if (numberHelpers.isNumber(fieldValues?.creditLimit) && newCurrencyPrecision !== currencyPrecision) {
                onFieldChange('creditLimit')(setCurrencyPrecision(fieldValues.creditLimit, newCurrencyPrecision));
            }

            onFieldChange('currency')(value);
        },
        [currencyPrecision, fieldValues?.creditLimit, onFieldChange]
    );

    if (!fieldValues) {
        return null;
    }

    return (
        <>
            <SectionHeader $noMarginTop={!isPopup}>{messages.primaryInformationSection}</SectionHeader>

            <>
                <FieldsWrapper $hasGap={isPopup}>
                    <RadioGroupEditorLabel $hasMarginRight={!isPopup}>Type:</RadioGroupEditorLabel>

                    <InlineRadioEditor
                        value={fieldValues.isPerson}
                        onChange={onFieldChange('isPerson')}
                        theme='no-layout'
                    >
                        <RadioGroupEditor.Item id={false}>{messages.isCompany}</RadioGroupEditor.Item>

                        <RadioGroupEditor.Item id={true}>{messages.isIndividual}</RadioGroupEditor.Item>
                    </InlineRadioEditor>
                </FieldsWrapper>

                <FieldsWrapper $hasGap={isPopup} $marginBottom={isPopup ? 10 : 32}>
                    {fieldValues.isPerson ? (
                        <>
                            <Field title={messages.companyName}>
                                <TextEditor
                                    value={fieldValues.companyName || ''}
                                    onChange={onFieldChange('companyName')}
                                    placeholder={messages.inputPlaceholder}
                                    maxLength={netSuiteConstants.companyNameMaxLength}
                                />
                            </Field>

                            {isFieldVisible(fieldSettings.salutation) && (
                                <Field
                                    title={messages.salutation}
                                    required={isFieldMandatory(fieldSettings.salutation)}
                                >
                                    <TextEditor
                                        value={fieldValues.salutation || ''}
                                        onChange={onFieldChange('salutation')}
                                        placeholder={messages.inputPlaceholder}
                                        maxLength={netSuiteConstants.salutationMaxLength}
                                    />
                                </Field>
                            )}

                            <Field title={messages.firstName} required>
                                <TextEditor
                                    value={fieldValues.firstName || ''}
                                    onChange={onFieldChange('firstName')}
                                    placeholder={messages.inputPlaceholder}
                                    invalid={showErrors && !fieldValues.firstName}
                                    maxLength={netSuiteConstants.firstNameMaxLength}
                                />
                            </Field>

                            <Field title={messages.middleName}>
                                <TextEditor
                                    value={fieldValues.middleName || ''}
                                    onChange={onFieldChange('middleName')}
                                    placeholder={messages.inputPlaceholder}
                                    maxLength={netSuiteConstants.middleNameMaxLength}
                                />
                            </Field>

                            <Field title={messages.lastName} required>
                                <TextEditor
                                    value={fieldValues.lastName || ''}
                                    onChange={onFieldChange('lastName')}
                                    placeholder={messages.inputPlaceholder}
                                    invalid={showErrors && !fieldValues.lastName}
                                    maxLength={netSuiteConstants.lastNameMaxLength}
                                />
                            </Field>

                            {isFieldVisible(fieldSettings.jobTitle) && (
                                <Field title={messages.jobTitle} required={isFieldMandatory(fieldSettings.jobTitle)}>
                                    <TextEditor
                                        value={fieldValues.jobTitle || ''}
                                        onChange={onFieldChange('jobTitle')}
                                        placeholder={messages.inputPlaceholder}
                                        invalid={isOptionalFieldInvalid(
                                            showErrors,
                                            fieldValues.jobTitle,
                                            fieldSettings.jobTitle
                                        )}
                                        maxLength={netSuiteConstants.jobTitleMaxLength}
                                    />
                                </Field>
                            )}
                        </>
                    ) : (
                        <>
                            <Field title={messages.companyName} required>
                                <TextEditor
                                    value={fieldValues.companyName || ''}
                                    onChange={onFieldChange('companyName')}
                                    placeholder={messages.inputPlaceholder}
                                    invalid={showErrors && !fieldValues.companyName}
                                    maxLength={netSuiteConstants.companyNameMaxLength}
                                />
                            </Field>

                            {isFieldVisible(fieldSettings.webAddress) && (
                                <Field
                                    title={messages.webAddress}
                                    required={isFieldMandatory(fieldSettings.webAddress)}
                                >
                                    <TextEditor
                                        value={fieldValues.webAddress || ''}
                                        onChange={onFieldChange('webAddress')}
                                        placeholder={messages.inputPlaceholder}
                                        invalid={
                                            isOptionalFieldInvalid(
                                                showErrors,
                                                fieldValues.webAddress,
                                                fieldSettings.webAddress
                                            ) ||
                                            (showErrors && !isVendorWebAddressValid(fieldValues.webAddress))
                                        }
                                        maxLength={netSuiteConstants.webAddressMaxLength}
                                    />
                                </Field>
                            )}
                        </>
                    )}

                    {isFieldVisible(fieldSettings.category) && (
                        <Field title={messages.category} required={isFieldMandatory(fieldSettings.category)}>
                            <NetSuiteVendorCategoriesDataProvider>
                                <DropdownEditor
                                    value={fieldValues.category || null}
                                    singleLinePerItem={false}
                                    onChange={onFieldChange('category')}
                                    placeholder={messages.selectPlaceholder}
                                    invalid={isOptionalFieldInvalid(
                                        showErrors,
                                        fieldValues.category,
                                        fieldSettings.category
                                    )}
                                    loading={false}
                                />
                            </NetSuiteVendorCategoriesDataProvider>
                        </Field>
                    )}

                    {isFieldVisible(fieldSettings.comments) && (
                        <Field title={messages.comments} required={isFieldMandatory(fieldSettings.comments)}>
                            <TextEditor
                                value={fieldValues.comments || ''}
                                onChange={onFieldChange('comments')}
                                placeholder={messages.inputPlaceholder}
                                invalid={isOptionalFieldInvalid(
                                    showErrors,
                                    fieldValues.comments,
                                    fieldSettings.comments
                                )}
                                maxLength={netSuiteConstants.commentsMaxLength}
                            />
                        </Field>
                    )}

                    {workflowId && <NetSuiteCustomFields workflowId={workflowId} showErrors={showErrors} />}
                </FieldsWrapper>
            </>

            {hasContactInformationSection && (
                <>
                    <SectionHeader $noMarginTop={!isPopup}>{messages.contactInformationSection}</SectionHeader>

                    <FieldsWrapper $hasGap={isPopup} $marginBottom={isPopup ? 10 : 32}>
                        {isFieldVisible(fieldSettings.email) && (
                            <Field title={messages.email} required={isFieldMandatory(fieldSettings.email)}>
                                <TextEditor
                                    value={fieldValues.email || ''}
                                    onChange={onFieldChange('email')}
                                    placeholder={messages.inputPlaceholder}
                                    invalid={isOptionalFieldInvalid(showErrors, fieldValues.email, fieldSettings.email)}
                                    maxLength={netSuiteConstants.emailMaxLength}
                                />
                            </Field>
                        )}

                        {isFieldVisible(fieldSettings.phone) && (
                            <Field title={messages.phone} required={isFieldMandatory(fieldSettings.phone)}>
                                <TextEditor
                                    value={fieldValues.phone || ''}
                                    onChange={onFieldChange('phone')}
                                    placeholder={messages.inputPlaceholder}
                                    invalid={isOptionalFieldInvalid(showErrors, fieldValues.phone, fieldSettings.phone)}
                                    maxLength={netSuiteConstants.phoneMaxLength}
                                />
                            </Field>
                        )}

                        {isFieldVisible(fieldSettings.altPhone) && (
                            <Field title={messages.altPhone} required={isFieldMandatory(fieldSettings.altPhone)}>
                                <TextEditor
                                    value={fieldValues.altPhone || ''}
                                    onChange={onFieldChange('altPhone')}
                                    placeholder={messages.inputPlaceholder}
                                    invalid={isOptionalFieldInvalid(
                                        showErrors,
                                        fieldValues.altPhone,
                                        fieldSettings.altPhone
                                    )}
                                    maxLength={netSuiteConstants.altPhoneMaxLength}
                                />
                            </Field>
                        )}

                        {isFieldVisible(fieldSettings.fax) && (
                            <Field title={messages.fax} required={isFieldMandatory(fieldSettings.fax)}>
                                <TextEditor
                                    value={fieldValues.fax || ''}
                                    onChange={onFieldChange('fax')}
                                    placeholder={messages.inputPlaceholder}
                                    invalid={isOptionalFieldInvalid(showErrors, fieldValues.fax, fieldSettings.fax)}
                                    maxLength={netSuiteConstants.faxMaxLength}
                                />
                            </Field>
                        )}
                    </FieldsWrapper>
                </>
            )}

            {hasFinancialInformationSection && (
                <>
                    <SectionHeader $noMarginTop={!isPopup}>{messages.financialInformationSection}</SectionHeader>

                    <FieldsWrapper $hasGap={isPopup} $marginBottom={isPopup ? 10 : 32}>
                        {isFieldVisible(fieldSettings.legalName) && (
                            <Field title={messages.legalName} required={isFieldMandatory(fieldSettings.legalName)}>
                                <TextEditor
                                    value={fieldValues.legalName || ''}
                                    onChange={onFieldChange('legalName')}
                                    placeholder={messages.inputPlaceholder}
                                    invalid={isOptionalFieldInvalid(
                                        showErrors,
                                        fieldValues.legalName,
                                        fieldSettings.legalName
                                    )}
                                    maxLength={netSuiteConstants.legalNameMaxLength}
                                />
                            </Field>
                        )}

                        {isFieldVisible(fieldSettings.accountNumber) && (
                            <Field
                                title={messages.accountNumber}
                                required={isFieldMandatory(fieldSettings.accountNumber)}
                            >
                                <TextEditor
                                    value={fieldValues.accountNumber || ''}
                                    onChange={onFieldChange('accountNumber')}
                                    placeholder={messages.inputPlaceholder}
                                    invalid={isOptionalFieldInvalid(
                                        showErrors,
                                        fieldValues.accountNumber,
                                        fieldSettings.accountNumber
                                    )}
                                    maxLength={netSuiteConstants.accountNumberMaxLength}
                                />
                            </Field>
                        )}

                        {isFieldVisible(fieldSettings.defaultExpenseAccount) && (
                            <Field
                                title={messages.defaultExpenseAccount}
                                required={isFieldMandatory(fieldSettings.defaultExpenseAccount)}
                            >
                                <NetSuiteAccountsDataProvider
                                    group={domain.NetSuiteAccountTypeGroup.Expense}
                                    subsidiaryId={fieldValues.primarySubsidiary?.id}
                                >
                                    <DropdownEditor
                                        value={fieldValues.defaultExpenseAccount || null}
                                        singleLinePerItem={false}
                                        onChange={onFieldChange('defaultExpenseAccount')}
                                        placeholder={messages.selectPlaceholder}
                                        invalid={isOptionalFieldInvalid(
                                            showErrors,
                                            fieldValues.defaultExpenseAccount,
                                            fieldSettings.defaultExpenseAccount
                                        )}
                                        loading={false}
                                    />
                                </NetSuiteAccountsDataProvider>
                            </Field>
                        )}

                        {isFieldVisible(fieldSettings.defaultPayablesAccount) && (
                            <Field
                                title={messages.defaultPayablesAccount}
                                required={isFieldMandatory(fieldSettings.defaultPayablesAccount)}
                            >
                                <NetSuiteAccountsDataProvider
                                    group={domain.NetSuiteAccountTypeGroup.Payable}
                                    subsidiaryId={fieldValues.primarySubsidiary?.id}
                                >
                                    <DropdownEditor
                                        value={fieldValues.defaultPayablesAccount || null}
                                        singleLinePerItem={false}
                                        onChange={onFieldChange('defaultPayablesAccount')}
                                        placeholder={messages.selectPlaceholder}
                                        invalid={isOptionalFieldInvalid(
                                            showErrors,
                                            fieldValues.defaultPayablesAccount,
                                            fieldSettings.defaultPayablesAccount
                                        )}
                                        loading={false}
                                    />
                                </NetSuiteAccountsDataProvider>
                            </Field>
                        )}

                        {isFieldVisible(fieldSettings.terms) && (
                            <Field title={messages.terms} required={isFieldMandatory(fieldSettings.terms)}>
                                <PlainDataProvider items={terms}>
                                    <DropdownEditor
                                        value={fieldValues.terms || null}
                                        singleLinePerItem={false}
                                        onChange={onFieldChange('terms')}
                                        placeholder={messages.selectPlaceholder}
                                        invalid={isOptionalFieldInvalid(
                                            showErrors,
                                            fieldValues.terms,
                                            fieldSettings.terms
                                        )}
                                        loading={false}
                                    />
                                </PlainDataProvider>
                            </Field>
                        )}

                        {isFieldVisible(fieldSettings.creditLimit) && (
                            <Field title={messages.creditLimit} required={isFieldMandatory(fieldSettings.creditLimit)}>
                                <NumberEditor
                                    value={fieldValues.creditLimit || null}
                                    onChange={onFieldChange('creditLimit')}
                                    placeholder={messages.inputPlaceholder}
                                    invalid={
                                        showErrors &&
                                        isFieldMandatory(fieldSettings.creditLimit) &&
                                        !numberHelpers.isNumber(fieldValues.creditLimit) &&
                                        !numberHelpers.isNumber(primarySubsidiaryItem?.creditLimit)
                                    }
                                    max={netSuiteConstants.maxCreditLimit}
                                    precision={currencyPrecision}
                                    allowFloat
                                />
                            </Field>
                        )}

                        {isFieldVisible(fieldSettings.incoterm) && (
                            <Field title={messages.incoterm} required={isFieldMandatory(fieldSettings.incoterm)}>
                                <NetSuiteIncotermDataProvider>
                                    <DropdownEditor
                                        value={fieldValues.incoterm || null}
                                        singleLinePerItem={false}
                                        onChange={onFieldChange('incoterm')}
                                        placeholder={messages.selectPlaceholder}
                                        invalid={isOptionalFieldInvalid(
                                            showErrors,
                                            fieldValues.incoterm,
                                            fieldSettings.incoterm
                                        )}
                                        loading={false}
                                    />
                                </NetSuiteIncotermDataProvider>
                            </Field>
                        )}

                        {isFieldVisible(fieldSettings.taxNumber) &&
                            defaultSubsidiary?.edition !== backend.NetSuiteSubsidiaryEditionType.CA && (
                                <Field title={messages.taxNumber} required={isFieldMandatory(fieldSettings.taxNumber)}>
                                    <TextEditor
                                        value={fieldValues.taxNumber || ''}
                                        onChange={onFieldChange('taxNumber')}
                                        placeholder={messages.inputPlaceholder}
                                        invalid={isOptionalFieldInvalid(
                                            showErrors,
                                            fieldValues.taxNumber,
                                            fieldSettings.taxNumber
                                        )}
                                        maxLength={netSuiteConstants.taxNumberMaxLength}
                                    />
                                </Field>
                            )}

                        {isFieldVisible(fieldSettings.is1099Eligible) && (
                            <Field title={messages.is1099Eligible}>
                                <CheckboxEditor
                                    value={fieldValues.is1099Eligible || false}
                                    onChange={onFieldChange('is1099Eligible')}
                                />
                            </Field>
                        )}

                        {isFieldVisible(fieldSettings.isJobResource) && (
                            <Field title={messages.isJobResource}>
                                <CheckboxEditor
                                    value={fieldValues.isJobResource || false}
                                    onChange={onFieldChange('isJobResource')}
                                />
                            </Field>
                        )}
                    </FieldsWrapper>
                </>
            )}

            <SectionHeader $noMarginTop={!isPopup}>{messages.subsidiariesSection}</SectionHeader>

            <FieldsWrapper $hasGap={isPopup}>
                <Field title={messages.primarySubsidiary} required>
                    <PlainDataProvider items={subsidiaryReferenceList?.values || []}>
                        <DropdownEditor
                            value={fieldValues.primarySubsidiary || null}
                            singleLinePerItem={false}
                            onChange={onPrimarySubsidiaryChange}
                            placeholder={messages.selectPlaceholder}
                            loading={isSubsidiaryFetching}
                            displayAttribute='name'
                            invalid={showErrors && !fieldValues.primarySubsidiary}
                        />
                    </PlainDataProvider>
                </Field>
            </FieldsWrapper>

            {isFieldVisible(fieldSettings.subsidiaries) && (
                <Box spacing={isPopup ? '0' : '0 0 32'}>
                    <StyledLineItemsTable
                        lineItems={fieldValues.subsidiaries || []}
                        columnDefinitions={subsidiaryTableDef}
                        canAddLine
                        canReorder={false}
                        onAddLine={onSubsidiaryListItemAdd}
                        onRemoveLine={onSubsidiaryListItemRemove}
                        onCloneLine={onSubsidiaryItemClone}
                        onReorder={miscHelpers.noop}
                    />
                </Box>
            )}

            <SectionHeader $noMarginTop={!isPopup}>{messages.currenciesSection}</SectionHeader>

            <FieldsWrapper $hasGap={isPopup} $marginBottom={isPopup ? 10 : 32}>
                <Field title={messages.primaryCurrency} required={isFieldMandatory(fieldSettings.currency)}>
                    <PlainDataProvider items={currencies}>
                        <DropdownEditor
                            value={fieldValues.currency || null}
                            singleLinePerItem={false}
                            onChange={onPrimaryCurrencyChange}
                            placeholder={messages.selectPlaceholder}
                            invalid={isOptionalFieldInvalid(showErrors, fieldValues.currency, fieldSettings.currency)}
                            loading={false}
                        />
                    </PlainDataProvider>
                </Field>

                {isFieldVisible(fieldSettings.currencies) && (
                    <CurrenciesField title={messages.currenciesList}>
                        <PlainDataProvider items={currencies}>
                            <DropdownEditor
                                value={fieldValues.currencies || null}
                                singleLinePerItem={false}
                                onChange={onFieldChange('currencies')}
                                placeholder={messages.selectPlaceholder}
                                loading={false}
                                multiple
                            />
                        </PlainDataProvider>
                    </CurrenciesField>
                )}
            </FieldsWrapper>

            <SectionHeader $noMarginTop={!isPopup}>{messages.addressesSection}</SectionHeader>

            <StyledAddressLineItemsTable
                lineItems={fieldValues.addressLines || []}
                columnDefinitions={addressTableDef}
                canAddLine
                canReorder={false}
                onAddLine={onAddressListItemAdd}
                onRemoveLine={onAddressListItemRemove}
                onCloneLine={onAddressItemClone}
                onReorder={miscHelpers.noop}
            />
        </>
    );
});

NetSuiteVendorForm.displayName = 'NetSuiteVendorForm';
